/** Small pieces used across the web pages. */

import React from 'react';
import type { Dict } from '../shared/i18n';
import { t as tr } from '../shared/i18n';
import { FREE_LIMITS } from '../shared/config';
import type { Entitlement, Countable } from '../shared/entitlements';
import { BRAND } from '../shared/config';

export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-4 ${className}`}>{children}</div>
);

export const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{children}</span>
);

export const Empty: React.FC<{ icon: string; title: string; hint?: string }> = ({ icon, title, hint }) => (
  <div className="flex flex-col items-center gap-2 py-16 text-center">
    <span className="text-4xl opacity-30" aria-hidden="true">{icon}</span>
    <p className="text-sm font-extrabold text-slate-400">{title}</p>
    {hint && <p className="text-xs text-slate-400 max-w-[240px]">{hint}</p>}
  </div>
);

/** Trial countdown / licence problem, shown at the top of the dashboard. */
export const StatusBanner: React.FC<{
  entitlement: Entitlement | null;
  t: Dict;
  onUpgrade: () => void;
}> = ({ entitlement, t, onUpgrade }) => {
  if (!entitlement) return null;

  const problem =
    entitlement.problem === 'expired' ? t.licenseExpired
    : entitlement.problem === 'wrong-device' ? t.licenseWrongDevice
    : null;

  if (problem) {
    return (
      <Banner tone="warn" text={problem} action={t.upgrade} onAction={onUpgrade} />
    );
  }

  if (entitlement.status === 'trial') {
    const last = entitlement.trialDaysLeft <= 1;
    return (
      <Banner
        tone={last ? 'warn' : 'info'}
        text={last ? t.trialLastDay : tr(t, 'trialBanner', { n: entitlement.trialDaysLeft })}
        action={t.upgrade}
        onAction={onUpgrade}
      />
    );
  }

  return null;
};

const Banner: React.FC<{
  tone: 'info' | 'warn';
  text: string;
  action?: string;
  onAction?: () => void;
}> = ({ tone, text, action, onAction }) => (
  <div className={`rounded-xl px-4 py-3 flex items-center justify-between gap-3 ${
    tone === 'warn' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-[#C1272D]'
  }`}>
    <span className="text-xs font-bold">{text}</span>
    {action && onAction && (
      <button onClick={onAction} className="text-xs font-extrabold underline shrink-0">{action}</button>
    )}
  </div>
);

const LIMIT_KEY: Record<Countable, { key: 'limitClients' | 'limitProducts' | 'limitOrders'; n: number }> = {
  clients: { key: 'limitClients', n: FREE_LIMITS.maxClients! },
  products: { key: 'limitProducts', n: FREE_LIMITS.maxProducts! },
  ordersThisMonth: { key: 'limitOrders', n: FREE_LIMITS.maxOrdersPerMonth! },
};

/** Shown in place of the "add" button once a free-plan ceiling is reached. */
export const LimitNotice: React.FC<{ what: Countable; t: Dict; onUpgrade: () => void }> = ({ what, t, onUpgrade }) => {
  const copy = LIMIT_KEY[what];
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-2">
      <p className="text-sm font-extrabold text-amber-900">{t.limitReached}</p>
      <p className="text-xs text-amber-800">{tr(t, copy.key, { n: copy.n })}</p>
      <button
        onClick={onUpgrade}
        className="w-full bg-[#C1272D] text-white py-3 rounded-xl font-extrabold text-sm mt-1"
      >
        {t.upgrade}
      </button>
    </div>
  );
};

/** Full-panel lock for analytics and export on the free plan. */
export const LockedPanel: React.FC<{ title: string; t: Dict; onUpgrade: () => void }> = ({ title, t, onUpgrade }) => (
  <div className="space-y-4">
    <Card className="text-center py-10 space-y-3">
      <span className="text-4xl" aria-hidden="true">🔒</span>
      <h2 className="text-lg font-extrabold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 max-w-[280px] mx-auto">{t.upgradeBlurb}</p>
    </Card>
    <button onClick={onUpgrade} className="w-full bg-[#C1272D] text-white py-4 rounded-2xl font-extrabold">
      {t.upgrade}
    </button>
    <a
      href={`${BRAND.siteUrl}/#pricing`}
      target="_blank"
      rel="noopener noreferrer"
      className="block text-center text-xs text-slate-400 hover:underline"
    >
      {BRAND.domain}
    </a>
  </div>
);
